import type { PasswordLimitsFragment } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import type { FC, ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { Button } from '../../ui/button';
import { notify } from '../../ui/notification';
import { AuthPageContainer } from './auth-page-container';
import { SetPassword } from './set-password';

export const SetPasswordPage: FC<{
  passwordLimits: PasswordLimitsFragment;
  onSetPassword: (password: string) => Promise<void>;
  onOpenAffine: () => void;
  journey?: ReactNode;
}> = ({
  passwordLimits,
  onSetPassword: propsOnSetPassword,
  onOpenAffine,
  journey,
}) => {
  const t = useI18n();
  const [hasSetUp, setHasSetUp] = useState(false);

  const onSetPassword = useCallback(
    (passWord: string) => {
      propsOnSetPassword(passWord)
        .then(() => setHasSetUp(true))
        .catch(e =>
          notify.error({
            title: t['com.streamlain.auth.password.set-failed'](),
            message: String(e),
          })
        );
    },
    [propsOnSetPassword, t]
  );

  return (
    <AuthPageContainer
      title={
        hasSetUp
          ? t['com.streamlain.auth.set.password.page.success']()
          : t['com.streamlain.auth.set.password.page.title']()
      }
      subtitle={
        hasSetUp
          ? t['com.streamlain.auth.sent.set.password.success.message']()
          : t['com.streamlain.auth.page.sent.email.subtitle']({
              min: String(passwordLimits.minLength),
              max: String(passwordLimits.maxLength),
            })
      }
    >
      {hasSetUp ? (
        <>
          <Button variant="primary" size="large" onClick={onOpenAffine}>
            {t['com.streamlain.auth.open.streamlain']()}
          </Button>
          {journey}
        </>
      ) : (
        <SetPassword
          passwordLimits={passwordLimits}
          onSetPassword={onSetPassword}
          progress={journey}
        />
      )}
    </AuthPageContainer>
  );
};
